"use client";

// Un éditeur de texte Kanon : `kan write -n` vérifie le brouillon pendant la frappe, avec les documents qui le lient,
// sans rien écrire dans la base ; et un clic sur un diagnostic place le curseur sur sa ligne. L'interface ne dit que
// « vérification », sans nommer l'outil.

import { useEffect, useRef, useState } from "react";
import type { Diagnostic } from "@/lib/api";

type Lint = (texte: string) => Promise<{ ok: boolean; diagnostics: Diagnostic[] }>;

// Les dossiers des documents qu'on édite : une erreur ailleurs (un document qui les lie) dit d'où elle vient.
const EDITES = ["memoires/", "analyses/", "entreprise/", "dossiers/"];
// Un fichier dit sans son extension : « affaires/local-a ».
const nomDocument = (fichier: string) => fichier.replace(/\.kan$/, "");

export function EditeurKanon({
  valeur,
  onChange,
  lint,
  lectureSeule = false,
  lignes = 18,
  diagnosticsServeur,
}: {
  valeur: string;
  onChange: (texte: string) => void;
  lint: Lint;
  lectureSeule?: boolean;
  lignes?: number;
  diagnosticsServeur?: Diagnostic[]; // ceux d'un enregistrement refusé
}) {
  const zone = useRef<HTMLTextAreaElement>(null);
  const [etat, setEtat] = useState<"verification" | "ok" | "erreurs" | "indisponible">("verification");
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);

  useEffect(() => {
    let annule = false;
    setEtat("verification");
    const minuteur = setTimeout(async () => {
      try {
        const r = await lint(valeur);
        if (annule) return;
        setDiagnostics(r.diagnostics);
        setEtat(r.ok ? "ok" : "erreurs");
      } catch {
        if (!annule) setEtat("indisponible");
      }
    }, 400);
    return () => {
      annule = true;
      clearTimeout(minuteur);
    };
  }, [valeur, lint]);

  const aller = (ligne: number | null) => {
    const z = zone.current;
    if (!z || !ligne) return;
    const lignesTexte = valeur.split("\n");
    const debut = lignesTexte.slice(0, ligne - 1).reduce((n, l) => n + l.length + 1, 0);
    const fin = debut + (lignesTexte[ligne - 1]?.length ?? 0);
    z.focus();
    z.setSelectionRange(debut, fin);
    const hauteurLigne = z.scrollHeight / Math.max(lignesTexte.length, 1);
    z.scrollTop = Math.max(0, (ligne - 3) * hauteurLigne);
  };

  const affiches = diagnosticsServeur?.length ? diagnosticsServeur : diagnostics;
  const erreurs = affiches.filter((d) => d.level === "error").length;

  return (
    <div className="editeur">
      <textarea
        ref={zone}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        readOnly={lectureSeule}
        rows={lignes}
        spellCheck={false}
      />
      <div className={`lint lint-${etat}`}>
        <span className="lint-etat">
          {etat === "verification" && "Vérification…"}
          {etat === "ok" && "Vérifié : aucune erreur"}
          {etat === "erreurs" && `${erreurs} erreur${erreurs > 1 ? "s" : ""} à corriger`}
          {etat === "indisponible" && "Vérification impossible : l'API ne répond pas"}
        </span>
        {affiches.length > 0 && (
          <ul>
            {affiches.map((d, i) => (
              <li key={i} className={`diag diag-${d.level}`} onClick={() => aller(d.line)}>
                <span className="diag-pos">
                  {d.line ? `ligne ${d.line}` : nomDocument(d.file)}
                  {d.line && !EDITES.some((p) => d.file.startsWith(p)) ? ` (${nomDocument(d.file)})` : ""}
                </span>
                {d.message}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
